import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import sortBy from 'lodash/sortBy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/free-solid-svg-icons/faSort';
import { faSortDown } from '@fortawesome/free-solid-svg-icons/faSortDown';
import { faSortUp } from '@fortawesome/free-solid-svg-icons/faSortUp';
import { faFileUser } from '@fortawesome/pro-solid-svg-icons/faFileUser';
import { Tooltip } from '../../components/ui';
import { Input } from '../../components/ui/Input';
import { Tag } from '../../components/ui/Tag';
import { snakeCaseToHumanReadable } from '../../utils';
import { Computation, EnrichmentCategory, PersonModelScore } from '../types';
import Clearbit from './icons/clearbit.svg';
import HGinsights from './icons/hginsights.svg';
import PredictLeads from './icons/predictleads.svg';
import { extractDoubleQuotedWords, isBooleanComputation } from './utils';
import { getStudioBaseUrl } from '../../utils/studioUrl';

const sourceIcons = {
  Clearbit: <Clearbit width={20} height={20} />,
  'HG Insights': <HGinsights width={20} height={20} />,
  PredictLeads: <PredictLeads width={20} height={20} />,
  MadKudu: <HGinsights width={20} height={20} />,
};

const CATEGORIES: { label: ReactNode; value: EnrichmentCategory }[] = [
  { label: 'All', value: 'all' },
  {
    label: (
      <>
        <svg
          width="12"
          height="14"
          viewBox="0 0 12 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.5 8H3.5C1.56752 8 0 9.56688 0 11.5V12.5C0 12.8981 0.15875 13.2794 0.44 13.5606C0.721248 13.8419 1.1025 14 1.5 14H10.5C10.8981 14 11.28 13.8419 11.5612 13.5606C11.8425 13.2794 12 12.8981 12 12.5V11.5C12 9.56688 10.4331 8 8.5 8ZM6 0C4.0688 0 2.5 1.56875 2.5 3.5C2.5 5.43125 4.06875 7 6 7C7.93248 7 9.5 5.43125 9.5 3.5C9.5 1.56875 7.9325 0 6 0Z"
            fill="currentColor"
          />
        </svg>
        Person
      </>
    ),
    value: 'person',
  },
  {
    label: (
      <>
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M13.7502 0C13.9224 0 14.0631 0.139751 14.0631 0.31202V10.6255H14.688C14.8603 10.6255 15 10.7653 15 10.9375V14.688C15 14.8602 14.8602 15 14.688 15H8.90633V11.5625C8.90633 11.3982 8.77888 11.2628 8.61714 11.2514L8.59341 11.2505H6.40658C6.24133 11.2505 6.10686 11.378 6.09455 11.5397L6.09367 11.5625V15H0.312023C0.139748 15 0 14.8603 0 14.688V10.9375C0 10.7653 0.139752 10.6255 0.312023 10.6255H0.936945V0.312032C0.936945 0.139758 1.07758 1.24459e-05 1.24985 1.24459e-05L13.7502 0ZM5.00016 1.87477C5.17243 1.87477 5.31218 2.01452 5.31218 2.18767V3.43752C5.31218 3.6098 5.17243 3.74954 5.00016 3.74954H3.12449C2.95222 3.74954 2.81247 3.60979 2.81247 3.43752V2.18767C2.81247 2.01452 2.95222 1.87477 3.12449 1.87477H5.00016ZM5.00016 4.68738C5.17243 4.68738 5.31218 4.82713 5.31218 5.00027V6.25013C5.31218 6.4224 5.17243 6.56215 5.00016 6.56215H3.12449C2.95222 6.56215 2.81247 6.4224 2.81247 6.25013V5.00027C2.81247 4.82712 2.95222 4.68738 3.12449 4.68738H5.00016ZM5.00016 7.49998C5.17243 7.49998 5.31218 7.63973 5.31218 7.81288V9.06273C5.31218 9.23501 5.17243 9.37475 5.00016 9.37475H3.12449C2.95222 9.37475 2.81247 9.235 2.81247 9.06273V7.81288C2.81247 7.63973 2.95222 7.49998 3.12449 7.49998H5.00016ZM8.43765 1.87477C8.60992 1.87477 8.74967 2.01452 8.74967 2.18767V3.43752C8.74967 3.6098 8.60992 3.74954 8.43765 3.74954H6.56198C6.38971 3.74954 6.24996 3.60979 6.24996 3.43752V2.18767C6.24996 2.01452 6.38971 1.87477 6.56198 1.87477H8.43765ZM8.43765 4.68738C8.60992 4.68738 8.74967 4.82713 8.74967 5.00027V6.25013C8.74967 6.4224 8.60992 6.56215 8.43765 6.56215H6.56198C6.38971 6.56215 6.24996 6.4224 6.24996 6.25013V5.00027C6.24996 4.82712 6.38971 4.68738 6.56198 4.68738H8.43765ZM8.43765 7.49998C8.60992 7.49998 8.74967 7.63973 8.74967 7.81288V9.06273C8.74967 9.23501 8.60992 9.37475 8.43765 9.37475H6.56198C6.38971 9.37475 6.24996 9.235 6.24996 9.06273V7.81288C6.24996 7.63973 6.38971 7.49998 6.56198 7.49998H8.43765ZM11.8751 1.87477C12.0474 1.87477 12.1872 2.01452 12.1872 2.18767V3.43752C12.1872 3.6098 12.0474 3.74954 11.8751 3.74954H9.99947C9.8272 3.74954 9.68745 3.60979 9.68745 3.43752V2.18767C9.68745 2.01452 9.8272 1.87477 9.99947 1.87477H11.8751ZM11.8751 4.68738C12.0474 4.68738 12.1872 4.82713 12.1872 5.00027V6.25013C12.1872 6.4224 12.0474 6.56215 11.8751 6.56215H9.99947C9.8272 6.56215 9.68745 6.4224 9.68745 6.25013V5.00027C9.68745 4.82712 9.8272 4.68738 9.99947 4.68738H11.8751ZM11.8751 7.49998C12.0474 7.49998 12.1872 7.63973 12.1872 7.81288V9.06273C12.1872 9.23501 12.0474 9.37475 11.8751 9.37475H9.99947C9.8272 9.37475 9.68745 9.235 9.68745 9.06273V7.81288C9.68745 7.63973 9.8272 7.49998 9.99947 7.49998H11.8751Z"
            fill="currentColor"
          />
        </svg>
        Company
      </>
    ),
    value: 'company',
  },
  {
    label: (
      <>
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14.6874 6.14282L13.386 6.00235V6.00161C13.2509 5.986 13.1388 5.89087 13.101 5.7608C12.9911 5.41743 12.8508 5.0852 12.6815 4.76784C12.6192 4.64892 12.6325 4.50399 12.7171 4.39845L13.539 3.37873C13.6481 3.24346 13.6384 3.04725 13.516 2.92314L12.0713 1.47978C11.9502 1.35864 11.7572 1.34749 11.6228 1.45452L10.5984 2.27727C10.4937 2.362 10.3489 2.37686 10.2294 2.31518C9.91312 2.14498 9.58203 2.00451 9.23979 1.89525C9.11136 1.85438 9.01929 1.74141 9.00519 1.60763L8.86488 0.304733C8.84632 0.131559 8.70081 0.000742043 8.5271 0H6.47511C6.30065 0 6.1544 0.131553 6.13583 0.304733L5.99552 1.60763H5.99478C5.98364 1.74661 5.88787 1.86405 5.75425 1.90344C5.412 2.01195 5.0809 2.15242 4.76465 2.32336C4.64512 2.38579 4.50036 2.37092 4.39568 2.28545L3.3749 1.4627C3.23904 1.35344 3.04305 1.36385 2.91982 1.48797L1.47811 2.93206C1.35561 3.05544 1.34522 3.25091 1.45287 3.38691L2.27469 4.40663V4.40738C2.36006 4.51143 2.37491 4.6571 2.31255 4.77602C2.1418 5.09339 2.00149 5.42561 1.8931 5.76899C1.85227 5.89682 1.73943 5.98823 1.6058 6.00163L0.304387 6.14284C0.131409 6.16142 0 6.30784 0 6.48251V8.53683C0.00148468 8.71 0.132145 8.85568 0.304387 8.87499L1.6058 9.01546C1.73943 9.02884 1.85228 9.12025 1.8931 9.2481C2.00149 9.59148 2.14181 9.9237 2.31255 10.2411C2.37491 10.36 2.36006 10.5057 2.27469 10.6105L1.45287 11.6361C1.34448 11.7714 1.35561 11.9669 1.47811 12.0895L2.92873 13.5418C3.05123 13.6659 3.24796 13.677 3.38307 13.567L4.40386 12.7443C4.50853 12.6595 4.6533 12.6447 4.77282 12.7064C5.08684 12.8699 5.41573 13.0029 5.75427 13.1055C5.88196 13.1464 5.97326 13.2593 5.98664 13.3924L6.12696 14.6953V14.696C6.14626 14.8692 6.29251 15 6.46698 15H8.51896C8.69268 15 8.83819 14.8684 8.856 14.696L8.99631 13.3931L8.99706 13.3924C9.01265 13.2564 9.10841 13.1434 9.23981 13.1055C9.58205 12.9962 9.91316 12.8558 10.2294 12.6856C10.3489 12.6239 10.4937 12.6387 10.5984 12.7235L11.6229 13.5626C11.758 13.6718 11.954 13.6614 12.0757 13.5373L13.5286 12.085C13.6466 11.9617 13.6548 11.7692 13.5471 11.6361L12.7253 10.6142C12.6414 10.5094 12.6273 10.3645 12.6897 10.2448C12.8589 9.92743 12.9992 9.59521 13.1091 9.25259C13.1499 9.1255 13.2613 9.03409 13.3942 9.01921L14.6956 8.87874C14.8686 8.8609 15 8.71523 15 8.54132V6.48262C14.9993 6.30573 14.8641 6.1577 14.6874 6.14282ZM7.50045 10.5873C6.68308 10.588 5.89912 10.264 5.32075 9.68574C4.74242 9.10824 4.41727 8.32338 4.41652 7.50584C4.41652 6.68754 4.74094 5.90269 5.31853 5.32442C5.89611 4.74616 6.68008 4.42139 7.49747 4.42139C8.3141 4.42214 9.09807 4.74767 9.67489 5.32665C10.2525 5.90563 10.5762 6.69049 10.5754 7.50883C10.5739 8.3249 10.2495 9.10677 9.67341 9.68435C9.09657 10.2611 8.31568 10.5858 7.50045 10.5873Z"
            fill="currentColor"
          />
        </svg>
        Tech
      </>
    ),
    value: 'tech',
  },
  {
    label: (
      <>
        <svg
          width="14"
          height="13"
          viewBox="0 0 14 13"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 3.95645C14 3.64416 13.759 3.39123 13.4615 3.39123H0.538462C0.240955 3.39123 0 3.64416 0 3.95645V12.4348C0 12.7471 0.240955 13 0.538462 13H13.4615C13.759 13 14 12.7471 14 12.4348V3.95645ZM5.65376 10.1739H8.34607C8.64357 10.1739 8.88453 9.92096 8.88453 9.60867C8.88453 9.29638 8.64357 9.04345 8.34607 9.04345H5.65376C5.35625 9.04345 5.1153 9.29638 5.1153 9.60867C5.1153 9.92096 5.35625 10.1739 5.65376 10.1739ZM5.65376 7.913H8.34607C8.64357 7.913 8.88453 7.66007 8.88453 7.34778C8.88453 7.03549 8.64357 6.78256 8.34607 6.78256H5.65376C5.35625 6.78256 5.1153 7.03549 5.1153 7.34778C5.1153 7.66007 5.35625 7.913 5.65376 7.913ZM3.23077 2.7759V0.569457C3.23077 0.395651 3.30481 0.240217 3.4226 0.137067L3.42327 0.13636C3.45625 0.105979 3.48721 0.084783 3.51481 0.0713588C3.5902 0.0282608 3.67702 0.00423934 3.76857 0.00423934H3.83722C3.81636 0.00141305 3.79482 0 3.77395 0L7.00007 0.00141306L10.2262 0C10.2053 0 10.1838 0.00141305 10.1629 0.00423934H10.2316C10.3231 0.00423934 10.4099 0.0282608 10.4853 0.0713588C10.5129 0.0847828 10.5439 0.105979 10.5769 0.13636L10.5775 0.137067C10.6953 0.240219 10.7694 0.395658 10.7694 0.569457L10.7687 2.7759H9.69245V1.13394L7.00014 1.13182L4.30783 1.13394V2.7759H3.23077Z"
            fill="currentColor"
          />
        </svg>
        Hiring
      </>
    ),
    value: 'hiring',
  },
  {
    label: (
      <>
        <svg
          width="18"
          height="18"
          version="1.1"
          viewBox="0 0 100 100"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g fill="currentColor">
            <path d="m12.5 37.5h42.434c1.7383 4.9922 6.4453 8.3359 11.734 8.3359 5.2852 0 9.9922-3.3438 11.73-8.3359h9.1016c2.3008 0 4.168-1.8672 4.168-4.168s-1.8672-4.1641-4.168-4.1641h-9.1016c-1.7383-4.9922-6.4453-8.3398-11.73-8.3398-5.2891 0-9.9961 3.3477-11.734 8.3398h-42.434c-2.3008 0-4.168 1.8633-4.168 4.1641s1.8672 4.168 4.168 4.168z" />
            <path d="m87.5 62.5h-42.434c-1.7383-4.9922-6.4453-8.3359-11.734-8.3359-5.2852 0-9.9922 3.3438-11.73 8.3359h-9.1016c-2.3008 0-4.168 1.8672-4.168 4.168s1.8672 4.1641 4.168 4.1641h9.1016c1.7383 4.9922 6.4453 8.3398 11.73 8.3398 5.2891 0 9.9961-3.3477 11.734-8.3398h42.434c2.3008 0 4.168-1.8633 4.168-4.1641s-1.8672-4.168-4.168-4.168z" />
          </g>
        </svg>
        Custom
      </>
    ),
    value: 'custom',
  },
];

function FilterButton<T>({
  label,
  filter,
  isSelected,
  onSelect,
}: {
  label: string | React.ReactNode;
  filter: T;
  isSelected: boolean;
  onSelect(category: T): void;
}) {
  return (
    <span
      onClick={() => onSelect(filter)}
      className={`${
        isSelected
          ? 'tw-border tw-border-transparent tw-bg-blue-600 tw-text-white hover:tw-bg-blue-600'
          : 'tw-border tw-border-gray-200 tw-bg-transparent tw-text-gray-600 hover:tw-bg-gray-100'
      } tw-inline-flex tw-h-10 tw-gap-2 tw-min-w-[5rem] tw-cursor-pointer tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-px-6 tw-text-md tw-font-medium`}
    >
      {label}
    </span>
  );
}

type Props = {
  personModelScore: PersonModelScore;
  computations: Computation[];
  tenant: number;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  setSelectedCategory: Dispatch<SetStateAction<EnrichmentCategory>>;
  selectedCategory: EnrichmentCategory;
};

export default function Enrichment({
  personModelScore,
  computations,
  tenant,
  search,
  setSearch,
  setSelectedCategory,
  selectedCategory,
}: Props) {
  const [orderCol, setOrderCol] = useState('name');
  const [orderDirection, setOrderDirection] = useState('ASC');
  const [onlyScored, setOnlyScored] = useState<boolean>(
    !!personModelScore.diagnosis
  );

  // TODO: Remove me when diagnosis up to every tenant
  useEffect(() => {
    if (!personModelScore.diagnosis) setOnlyScored(false);
    if (personModelScore.diagnosis) setOnlyScored(true);
  }, [personModelScore]);

  const getSortIcon = (column: string) => {
    if (column === orderCol && orderDirection === 'ASC') return faSortDown;
    if (column === orderCol && orderDirection === 'DESC') return faSortUp;

    return faSort;
  };

  const setComputationsOrder = (column: string) => {
    if (column === orderCol) {
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setOrderCol(column);
    }
  };

  const hydratedComputations: Computation[] = useMemo(() => {
    // TODO: Remove me when diagnosis up to every tenant
    if (!personModelScore.diagnosis) return computations;

    const computationScoredInTrees =
      Array.from(
        new Set(
          personModelScore?.diagnosis?.treesDiagnosis?.flatMap(
            ({ nodeComputationsValuesAndRules }: any) =>
              nodeComputationsValuesAndRules.map(
                ({ computation }: any) => computation
              )
          )
        )
      ) ?? [];

    const computationScoredInOverrides =
      Array.from(
        new Set(
          personModelScore.diagnosis.overrideDiagnosis.flatMap(
            ({ computationsValuesAndRules }: any) =>
              computationsValuesAndRules.map(
                ({ computation }: any) => computation
              )
          )
        )
      ) ?? [];

    const computationScoredInPoints =
      Array.from(
        new Set(
          personModelScore.diagnosis.microMlDiagnosis.flatMap(
            ({ computationsValuesAndRules }: any) =>
              computationsValuesAndRules.map(
                ({ computation }: any) => computation
              )
          )
        )
      ) ?? [];

    return computations?.map((computation: Computation) => ({
      ...computation,
      scoredInTrees: computationScoredInTrees.includes(computation.name),
      scoredInOverrides: computationScoredInOverrides.includes(
        computation.name
      ),
      scoredInPoints: computationScoredInPoints.includes(computation.name),
    }));
  }, [computations, personModelScore]);

  const isTreeBasedModel = hydratedComputations.some(
    ({ scoredInTrees }) => scoredInTrees
  );

  let filteredComputations: Computation[] = useMemo(
    () =>
      hydratedComputations?.filter(
        ({
          name,
          categoryName,
          scoredInOverrides,
          scoredInTrees,
          scoredInPoints,
        }) => {
          if (
            onlyScored &&
            personModelScore.diagnosis && // TODO: Remove me when diagnosis up to every tenant
            isTreeBasedModel &&
            !scoredInTrees &&
            !scoredInOverrides
          )
            return false;

          if (
            onlyScored &&
            personModelScore.diagnosis && // TODO: Remove me when diagnosis up to every tenant
            !isTreeBasedModel &&
            !scoredInPoints &&
            !scoredInOverrides
          )
            return false;

          // "" can be used to match the whole word, multiple "" will be handle as an OR
          const searchedNames = extractDoubleQuotedWords(
            search
          ).map((searchedName) => searchedName.toLowerCase());
          const readableName = snakeCaseToHumanReadable(name).toLowerCase();

          // multiple searched names, means advanced search => equals
          if (searchedNames.length) {
            const searchHasMatched = searchedNames.reduce(
              (matched, searchedName) =>
                matched || readableName === searchedName,
              false
            );
            if (!searchHasMatched) return false;
          } else if (!readableName.includes(search)) {
            // if only one searched name, means normal search => contains
            return false;
          }

          if (selectedCategory !== 'all') {
            if (selectedCategory === 'custom' && categoryName) return false;

            if (
              selectedCategory !== 'custom' &&
              categoryName !== selectedCategory
            )
              return false;
          }

          return true;
        }
      ),
    [
      search,
      selectedCategory,
      onlyScored,
      hydratedComputations,
      isTreeBasedModel,
    ]
  );

  filteredComputations = sortBy(filteredComputations, [orderCol]);

  if (orderDirection === 'DESC') {
    filteredComputations.reverse();
  }

  return (
    <div className="tw-mt-6">
      <div className="tw-flex tw-gap-2 tw-items-center tw-mt-14">
        <FontAwesomeIcon icon={faFileUser} size="xl" />
        <span className="tw-font-semibold tw-text-lg">Enrichment</span>
      </div>
      <div className="tw-mt-1">
        <a
          className="tw-text-blue-700 hover:tw-no-underline hover:tw-text-blue-800"
          href={`${getStudioBaseUrl()}/tenant/${tenant}/computations`}
          target="_blank"
          rel="noopener noreferrer"
        >
          View computations in HG Insights Studio &nbsp;
          <svg
            width="15"
            height="13"
            viewBox="0 0 15 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.9362 4.90489C14.9362 5.37703 14.3657 5.61345 14.0324 5.27809L12.5708 3.81848L8.94704 7.42044C8.8388 7.52869 8.6761 7.52869 8.56587 7.42044L7.62024 6.47482C7.512 6.36458 7.512 6.20188 7.62024 6.09364L11.244 2.49168L9.78236 1.03008C9.449 0.696722 9.68607 0.124298 10.1556 0.124298H14.4062C14.6971 0.124298 14.9382 0.365358 14.9382 0.656211L14.9362 4.90489Z"
              fill="currentColor"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M11.7493 7.82804V10.4843C11.7493 11.8031 10.6789 12.8736 9.35812 12.8736H2.45323C1.13441 12.8736 0.0620117 11.8032 0.0620117 10.4843V3.57736C0.0620117 2.25854 1.13447 1.18614 2.45323 1.18614H8.29562C8.44437 1.18614 8.56058 1.30235 8.56058 1.45309V1.98302C8.56058 2.13376 8.44636 2.24997 8.29562 2.24997H2.45323C1.7221 2.24997 1.12378 2.84629 1.12378 3.57743V10.4844C1.12378 11.2135 1.7221 11.8118 2.45323 11.8118H9.35812C10.0893 11.8118 10.6876 11.2135 10.6876 10.4844V7.82811C10.6876 7.67737 10.8038 7.56116 10.9525 7.56116H11.4844C11.6332 7.56116 11.7493 7.6773 11.7493 7.82804Z"
              fill="currentColor"
            />
          </svg>
        </a>
      </div>
      <div className="tw-mt-2 tw-mx-2">
        <div
          className="tw-grid tw-gap-x-10 tw-gap-y-5 tw-py-4"
          style={{ gridTemplateColumns: 'auto 1fr' }}
        >
          <span className="tw-my-auto tw-text-md tw-font-medium">
            Filter by category:
          </span>
          <div className="tw-inline-flex tw-items-center tw-gap-2">
            {CATEGORIES.map(({ label, value }, index) => (
              <FilterButton
                key={index}
                label={label}
                filter={value}
                isSelected={selectedCategory === value}
                onSelect={setSelectedCategory}
              />
            ))}
          </div>
          <span className="tw-my-auto tw-text-md tw-font-medium">
            Search computation:
          </span>
          <div className="tw-w-1/2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Enter a computation name"
            />
          </div>
          <span className="tw-my-auto tw-font-medium tw-text-md tw-flex tw-items-center">
            Impactful computations only
            <Tooltip
              text={
                <span className="tw-leading-normal">
                  Check this box if you want to show only traits that had an
                  impact on the score.
                </span>
              }
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 18 18"
                fill="none"
                className="tw-text-gray-300 tw-mx-1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_147_461)">
                  <path
                    d="M9.00934 3.70006e-06C11.3972 0.00211312 13.6857 0.953446 15.372 2.64438C17.0588 4.33469 18.0038 6.62616 18.0003 9.01404C17.9969 11.4019 17.0441 13.6899 15.3531 15.3752C13.6614 17.0606 11.3691 18.0049 8.98144 18C6.59428 17.9951 4.3063 17.0416 2.62168 15.3491C0.93699 13.6567 -0.00595854 11.3645 0.00052146 8.97678C0.00684954 6.5925 0.958877 4.3074 2.64778 2.62476C4.33739 0.941476 6.62542 -0.0021563 9.0097 3.70006e-06H9.00934ZM8.32169 6.84H6.71575C6.59903 6.84141 6.48301 6.85758 6.37051 6.88852C6.23269 6.91735 6.12792 7.02844 6.10754 7.16766C6.06958 7.31883 6.14551 7.47492 6.28754 7.53821C6.46051 7.61907 6.64614 7.66899 6.83669 7.68587C7.3373 7.70907 7.5384 7.92916 7.46318 8.44383C7.32255 9.40712 7.13412 10.3626 6.97169 11.3238C6.862 11.9721 6.76989 12.6218 6.66373 13.2694C6.5217 14.1427 7.33522 14.9612 7.98138 15.0694C8.36388 15.1236 8.75059 15.1482 9.13732 15.1433C9.96561 15.1426 10.7566 14.8002 11.324 14.1969C11.4273 14.102 11.5047 13.9824 11.549 13.8495C11.5708 13.7335 11.5433 13.6133 11.473 13.5184C11.3886 13.4649 11.2846 13.4537 11.1911 13.4874C11.0434 13.5359 10.9119 13.6365 10.7678 13.6998C10.5997 13.7785 10.4232 13.839 10.2418 13.8798C10.0963 13.9156 9.9416 13.8854 9.81996 13.7975C9.69761 13.7096 9.62027 13.5725 9.60831 13.4227C9.57808 13.2554 9.57245 13.0838 9.59214 12.9151C9.65331 12.4974 9.7398 12.0833 9.81504 11.6692C10.021 10.5406 10.2292 9.41069 10.4401 8.28011C10.4978 7.97073 10.5948 7.66628 10.5301 7.34073C10.5203 7.19588 10.4521 7.06088 10.3417 6.96737C10.2313 6.87315 10.0878 6.82745 9.94302 6.84011L8.32169 6.84ZM9.30114 2.83716C8.63739 2.80763 8.02215 3.18311 7.74443 3.78708C7.46669 4.39037 7.58201 5.10192 8.03623 5.58708C8.49044 6.07153 9.19287 6.23324 9.81302 5.99629C10.4339 5.75864 10.8487 5.16941 10.8635 4.50567C10.8888 3.61552 10.1906 2.87162 9.30114 2.84067V2.83716Z"
                    fill="currentColor"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_147_461">
                    <rect width="18" height="18" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </Tooltip>
            :
          </span>
          <div className="tw-w-full tw-flex tw-items-center tw-justify-between">
            <div className="custom-control custom-switch">
              <input
                type="checkbox"
                className="custom-control-input"
                id="customSwitch2"
                onChange={() => setOnlyScored((state) => !state)}
                checked={onlyScored}
              />
              <label
                className="custom-control-label my-auto cursor-pointer"
                htmlFor="customSwitch2"
              ></label>
            </div>

            <div className="-tw-mb-6 tw-text-right tw-text-sm tw-text-gray-300">
              {filteredComputations.length} computation
              {filteredComputations.length > 1 ? 's' : ''} found
            </div>
          </div>
        </div>
        <div className="tw-py-3">
          <div className="tw-h-[400px] tw-w-full tw-overflow-y-auto ">
            <TableVirtuoso
              components={{
                EmptyPlaceholder: () => (
                  <div className="tw-text-sm tw-text-center tw-py-16 tw-w-full tw-absolute">
                    No computations found
                  </div>
                ),
                Table: (props) => (
                  <table {...props} className="tw-w-full tw-table-fixed" />
                ),
                TableRow: (props) => (
                  <tr {...props} className="tw-h-12 tw-text-left" />
                ),
              }}
              style={{ height: 400 }}
              data={filteredComputations}
              fixedHeaderContent={() => (
                <tr className="tw-h-12 tw-text-left">
                  <th className="tw-w-[30%] tw-rounded-l-md tw-font-medium tw-px-3 tw-text-sm tw-py-2 tw-sticky tw-top-0 tw-bg-dark tw-text-white">
                    <span
                      className="tw-cursor-pointer tw-gap-2 tw-select-none"
                      onClick={() => setComputationsOrder('name')}
                    >
                      Name
                      <FontAwesomeIcon
                        icon={getSortIcon('name')}
                        width="22"
                        height="22"
                      />
                    </span>
                  </th>
                  <th className="tw-w-[40%] tw-px-2 tw-text-sm tw-font-medium tw-break-words tw-py-2 tw-sticky tw-top-0 tw-bg-dark tw-text-white">
                    <span
                      className="tw-cursor-pointer tw-gap-2 tw-select-none"
                      onClick={() => setComputationsOrder('value')}
                    >
                      Value
                      <FontAwesomeIcon
                        icon={getSortIcon('value')}
                        width="22"
                        height="22"
                      />
                    </span>
                  </th>
                  <th className="tw-w-[15%] tw-text-center tw-text-sm tw-font-medium tw-whitespace-nowrap tw-px-3 tw-py-2 tw-sticky tw-top-0 tw-bg-dark tw-text-white">
                    <span
                      className="tw-cursor-pointer tw-gap-2 tw-select-none"
                      onClick={() => setComputationsOrder('dataSource')}
                    >
                      Source
                      <FontAwesomeIcon
                        icon={getSortIcon('dataSource')}
                        width="22"
                        height="22"
                      />
                    </span>
                  </th>
                  <th className="tw-w-[15%] tw-rounded-r-md tw-text-center tw-text-sm tw-font-medium tw-whitespace-nowrap tw-px-3 tw-py-2 tw-sticky tw-top-0 tw-bg-dark tw-text-white">
                    Is used in...
                  </th>
                </tr>
              )}
              itemContent={(index, computation) => (
                <>
                  <td
                    className={`tw-w-[30%] tw-rounded-l-md tw-font-medium tw-px-3 tw-text-sm tw-py-2 ${
                      index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-light'
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-gap-1 tw-cursor-default">
                      <Tooltip
                        text={
                          <span className="tw-text-sm tw-leading-normal tw-cursor-default">
                            {computation.description}
                          </span>
                        }
                        visible={!!computation.description}
                      >
                        <div>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 18 18"
                            fill="none"
                            className={`tw-text-gray-300 tw-mx-1 ${
                              !computation.description ? 'tw-opacity-0' : ''
                            }`}
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_147_461)">
                              <path
                                d="M9.00934 3.70006e-06C11.3972 0.00211312 13.6857 0.953446 15.372 2.64438C17.0588 4.33469 18.0038 6.62616 18.0003 9.01404C17.9969 11.4019 17.0441 13.6899 15.3531 15.3752C13.6614 17.0606 11.3691 18.0049 8.98144 18C6.59428 17.9951 4.3063 17.0416 2.62168 15.3491C0.93699 13.6567 -0.00595854 11.3645 0.00052146 8.97678C0.00684954 6.5925 0.958877 4.3074 2.64778 2.62476C4.33739 0.941476 6.62542 -0.0021563 9.0097 3.70006e-06H9.00934ZM8.32169 6.84H6.71575C6.59903 6.84141 6.48301 6.85758 6.37051 6.88852C6.23269 6.91735 6.12792 7.02844 6.10754 7.16766C6.06958 7.31883 6.14551 7.47492 6.28754 7.53821C6.46051 7.61907 6.64614 7.66899 6.83669 7.68587C7.3373 7.70907 7.5384 7.92916 7.46318 8.44383C7.32255 9.40712 7.13412 10.3626 6.97169 11.3238C6.862 11.9721 6.76989 12.6218 6.66373 13.2694C6.5217 14.1427 7.33522 14.9612 7.98138 15.0694C8.36388 15.1236 8.75059 15.1482 9.13732 15.1433C9.96561 15.1426 10.7566 14.8002 11.324 14.1969C11.4273 14.102 11.5047 13.9824 11.549 13.8495C11.5708 13.7335 11.5433 13.6133 11.473 13.5184C11.3886 13.4649 11.2846 13.4537 11.1911 13.4874C11.0434 13.5359 10.9119 13.6365 10.7678 13.6998C10.5997 13.7785 10.4232 13.839 10.2418 13.8798C10.0963 13.9156 9.9416 13.8854 9.81996 13.7975C9.69761 13.7096 9.62027 13.5725 9.60831 13.4227C9.57808 13.2554 9.57245 13.0838 9.59214 12.9151C9.65331 12.4974 9.7398 12.0833 9.81504 11.6692C10.021 10.5406 10.2292 9.41069 10.4401 8.28011C10.4978 7.97073 10.5948 7.66628 10.5301 7.34073C10.5203 7.19588 10.4521 7.06088 10.3417 6.96737C10.2313 6.87315 10.0878 6.82745 9.94302 6.84011L8.32169 6.84ZM9.30114 2.83716C8.63739 2.80763 8.02215 3.18311 7.74443 3.78708C7.46669 4.39037 7.58201 5.10192 8.03623 5.58708C8.49044 6.07153 9.19287 6.23324 9.81302 5.99629C10.4339 5.75864 10.8487 5.16941 10.8635 4.50567C10.8888 3.61552 10.1906 2.87162 9.30114 2.84067V2.83716Z"
                                fill="currentColor"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_147_461">
                                <rect width="18" height="18" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                      </Tooltip>
                      <a
                        className="tw-text-[#182b52] hover:tw-no-underline hover:tw-text-blue-800"
                        href={`${getStudioBaseUrl()}/tenant/${tenant}/computations/${
                          computation.name
                        }`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View computation in Studio"
                      >
                        {snakeCaseToHumanReadable(computation.name)}
                      </a>
                    </div>
                  </td>
                  <td
                    className={`tw-w-[40%] tw-px-2 tw-text-sm tw-break-words tw-py-2 ${
                      index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-light'
                    }`}
                  >
                    {(computation.isBoolean ||
                      isBooleanComputation(computation.name)) &&
                      computation.value === 1 && (
                        <div className="tw-inline-flex tw-items-center tw-gap-2">
                          <svg
                            width="14"
                            height="11"
                            viewBox="0 0 14 11"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M12.4275 3.04816e-05C12.1358 0.00959716 11.8606 0.128652 11.6585 0.331693C9 3.10705 7 5.32077 4.67555 7.91589L1.84424 5.58584C1.61813 5.39876 1.32538 5.30629 1.02936 5.32966C0.733339 5.35305 0.459157 5.48911 0.266908 5.70914C0.0746593 5.92917 -0.0203606 6.21406 0.00365721 6.50213C0.0265959 6.79019 0.167506 7.05701 0.393608 7.24409L4.02886 10.2374C4.47343 10.6084 5.13647 10.5818 5.54938 10.1779C8.5 6.87428 10.5 4.66056 13.2488 1.87096C13.5819 1.5574 13.6824 1.07585 13.5 0.660237C13.3176 0.244626 12.8906 -0.0180384 12.4275 3.04816e-05Z"
                              fill="black"
                            />
                          </svg>

                          <span>Yes</span>
                        </div>
                      )}
                    {(computation.isBoolean ||
                      isBooleanComputation(computation.name)) &&
                      computation.value === 0 && (
                        <span className="tw-inline-flex tw-items-center tw-gap-2">
                          <svg
                            width="14"
                            height="11"
                            viewBox="0 0 11 11"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              fillRule="evenodd"
                              clipRule="evenodd"
                              d="M1.6486 0.285461L5.49592 4.14779L9.35825 0.285461C10.2518 -0.593067 11.6 0.755048 10.7064 1.6486L6.84405 5.49592L10.7064 9.35825C11.5999 10.2518 10.2518 11.6 9.35825 10.7064L5.49592 6.84405L1.6486 10.7064C0.755047 11.5999 -0.593105 10.2518 0.285461 9.35825L4.14779 5.49592L0.285461 1.6486C-0.593067 0.755048 0.755047 -0.593105 1.6486 0.285461Z"
                              fill="black"
                            />
                          </svg>
                          <span>No</span>
                        </span>
                      )}

                    {isBooleanComputation(computation.name) &&
                      computation.value !== 0 &&
                      computation.value !== 1 &&
                      computation.value}

                    {!computation.isBoolean &&
                      !isBooleanComputation(computation.name) &&
                      computation.value}
                  </td>
                  <td
                    className={`tw-w-[15%] tw-text-center tw-text-sm tw-text-gray-500 tw-whitespace-nowrap tw-px-3 tw-py-2 ${
                      index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-light'
                    }`}
                  >
                    <Tooltip text={computation.dataSource || 'HG Insights'}>
                      <span>
                        {sourceIcons[computation.dataSource || 'HG Insights']}
                      </span>
                    </Tooltip>
                  </td>
                  <td
                    className={`tw-w-[15%] tw-rounded-r-md tw-text-center tw-whitespace-nowrap tw-px-3 tw-py-2 ${
                      index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-light'
                    }`}
                  >
                    <div className="tw-flex tw-gap-2 tw-justify-center tw-items-center">
                      {computation.scoredInOverrides && (
                        <Tag color="orange">Override</Tag>
                      )}
                      {isTreeBasedModel && computation.scoredInTrees && (
                        <Tag color="green">Tree</Tag>
                      )}
                      {!isTreeBasedModel && computation.scoredInPoints && (
                        <Tag color="blue">Point</Tag>
                      )}
                    </div>
                  </td>
                </>
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
